import { useMemo } from 'react';
import { useChecklistData } from 'components/required-checklist/useChecklistData';
import { useAccountLocalStorage } from 'contexts/useAccountLocalStorage';
import { useFlags } from 'launchdarkly-react-client-sdk';

const useRequiredChecklistVisibility = () => {
    const { showRequiredChecklist, personalizedDashboard } = useFlags();
    const { completedAllActions, data } = useChecklistData();
    const {
        hasSeenChecklistCompleteDialog,
        hasSeenCompletedConfetti,
        completedRequiredChecklistThisSession,
        completedRequiredChecklistBetweenSessions,
        wasCompletedOverMaxTimeAgo,
    } = useAccountLocalStorage(
        ({
            hasSeenChecklistCompleteDialog,
            hasSeenCompletedConfetti,
            completedRequiredChecklistThisSession,
            completedRequiredChecklistBetweenSessions,
            wasCompletedOverMaxTimeAgo,
        }) => ({
            hasSeenChecklistCompleteDialog,
            hasSeenCompletedConfetti,
            completedRequiredChecklistThisSession,
            completedRequiredChecklistBetweenSessions,
            wasCompletedOverMaxTimeAgo,
        }),
    );

    const flagEnabled = useMemo(
        () => personalizedDashboard && showRequiredChecklist,
        [personalizedDashboard, showRequiredChecklist],
    );

    const hasData = useMemo(() => data.length > 0, [data]);

    const shouldShowChecklists = useMemo(
        () =>
            flagEnabled &&
            hasData &&
            (!hasSeenChecklistCompleteDialog || !completedAllActions) &&
            !wasCompletedOverMaxTimeAgo,
        [
            flagEnabled,
            hasData,
            hasSeenChecklistCompleteDialog,
            wasCompletedOverMaxTimeAgo,
            completedAllActions,
        ],
    );

    const shouldShowHomepageConfetti = useMemo(
        () =>
            flagEnabled &&
            hasData &&
            completedRequiredChecklistThisSession &&
            !hasSeenChecklistCompleteDialog,
        [
            flagEnabled,
            hasData,
            completedRequiredChecklistThisSession,
            hasSeenChecklistCompleteDialog,
        ],
    );

    const shouldShowChecklistCompleteDialog = useMemo(
        () =>
            flagEnabled &&
            hasData &&
            !hasSeenChecklistCompleteDialog &&
            completedAllActions &&
            !wasCompletedOverMaxTimeAgo &&
            (completedRequiredChecklistBetweenSessions ||
                (!completedRequiredChecklistBetweenSessions &&
                    completedRequiredChecklistThisSession &&
                    hasSeenCompletedConfetti)),
        [
            flagEnabled,
            hasData,
            hasSeenChecklistCompleteDialog,
            completedAllActions,
            wasCompletedOverMaxTimeAgo,
            completedRequiredChecklistBetweenSessions,
            completedRequiredChecklistThisSession,
            hasSeenCompletedConfetti,
        ],
    );

    return {
        shouldShowChecklists,
        shouldShowHomepageConfetti,
        shouldShowChecklistCompleteDialog,
    };
};

export { useRequiredChecklistVisibility };
